/**
 * Concordancia de género y número en castellano.
 *
 * Todo el texto del juego se monta a partir de estas formas: el pack guarda cada sala
 * como nombre desnudo (`"Sótano"`) más género y número, y aquí se derivan el artículo
 * y las contracciones (`al`, `del`). Así una plantilla nunca puede escribir
 * "en el Cocina".
 */

export type Gender = 'm' | 'f';

export interface Nominal {
  name: string;
  gender: Gender;
  /** `true` para nombres de forma plural: "Caballerizas", "Camerinos", "Cocinas". */
  plural: boolean;
}

/** Artículo determinado: `el`, `la`, `los`, `las`. */
export function article(noun: Pick<Nominal, 'gender' | 'plural'>): string {
  if (noun.plural) return noun.gender === 'm' ? 'los' : 'las';
  return noun.gender === 'm' ? 'el' : 'la';
}

/** Formas listas para interpolar de un sustantivo con artículo. */
export interface NounForms {
  /** "Sótano" */
  name: string;
  /** "el Sótano" — sujeto u objeto directo ("pisó el Sótano"). */
  def: string;
  /** "en el Sótano" */
  en: string;
  /** "al Sótano" — con contracción `a + el`. */
  al: string;
  /** "del Sótano" — con contracción `de + el`. */
  del: string;
}

export function nounForms(noun: Nominal): NounForms {
  const art = article(noun);
  const def = `${art} ${noun.name}`;
  return {
    name: noun.name,
    def,
    en: `en ${def}`,
    al: art === 'el' ? `al ${noun.name}` : `a ${def}`,
    del: art === 'el' ? `del ${noun.name}` : `de ${def}`,
  };
}

/** "el primero" / "la primera", según el género de la persona. */
export function firstOrdinal(gender: Gender): string {
  return gender === 'm' ? 'el primero' : 'la primera';
}

/** "el último" / "la última", según el género de la persona. */
export function lastOrdinal(gender: Gender): string {
  return gender === 'm' ? 'el último' : 'la última';
}
